import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, post } from '@/lib/api/client';
import type { BusinessAccount, BusinessAccountInput } from './types';

/**
 * Payout accounts.
 *
 * `accounts/create` and `accounts/update/{id}` take the same binding model.
 *
 * `AcceptanceIp` is deliberately never sent. The controller stamps the caller's
 * real address **only when the field arrives empty** — a value the client does
 * send is trusted and stored verbatim. Since that address is the evidence of
 * who accepted the payout terms and from where, the honest thing a client can
 * do is supply nothing and let the server record what it actually saw. The
 * original fetched its own address from `test/getip` and sent that, which is
 * the one case where the record is whatever the browser claimed.
 */

const KEY = ['business-accounts'] as const;

interface Paged<T> {
  results: T[];
}

export function useBusinessAccounts() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => get<Paged<BusinessAccount>>('businesses/accounts'),
    select: (data) =>
      [...(data.results ?? [])].sort((a, b) =>
        (a.businessName ?? '').localeCompare(b.businessName ?? '')
      ),
    staleTime: 60_000,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: KEY });
}

export function useSaveBusinessAccount() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({
      id,
      values,
    }: {
      id: string | null;
      values: BusinessAccountInput;
    }) =>
      post<BusinessAccount>(
        id ? `businesses/accounts/update/${id}` : 'businesses/accounts/create',
        values
      ),
    onSuccess: invalidate,
  });
}

/** Soft — the list filters on `DeletedAt == null`. */
export function useDeleteBusinessAccount() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (id: string) =>
      del<unknown>(`businesses/accounts/update/${id}`),
    onSuccess: invalidate,
  });
}
